/**
 * Who is in a person's life, derived from headers by code and never by a model (Batch C.3, D49).
 *
 * **What a header can say, and what it cannot.** An address, a display name, a date, who wrote to whom and
 * who sat in which meeting — all of that is in the envelope and the invitation, and all of it is arithmetic.
 * *Why* two people write to each other, what they mean to each other and what they owe each other are in
 * the body, and Autho holds no bodies (O45). So this derives exactly what the envelope carries and stops
 * there: it is the record (Mind), and it says `inferred` on every field it made up from a count.
 *
 * **Never a model, and the build counts it.** `FLOW_BUDGETS['me.relationships.derive']` declares zero model
 * calls; a derivation that started calling one would fail the build rather than the review (D49's rule).
 *
 * Nothing here is asynchronous, and nothing here reaches a database: it takes pointers in and hands people
 * out, so the rules are testable against headers alone — which is what the M2.75 row asks for.
 *
 * This file is imported by every process that imports `@autho/core`, including one under strip-only type
 * stripping (F123): no parameter properties, no enums.
 */

/** What the derivation reads. A subset of a `me_item` row, named so the rules cannot reach past it. */
export interface Pointer {
  id: string;
  surface: 'mail' | 'calendar' | 'drive';
  /** The instant that orders it. */
  at: Date;
  /** Everybody involved, as lower-cased addresses. */
  participants: readonly string[];
  /**
   * Who wrote it (mail) or organised it (calendar), when the header said. Null for a file, whose owners
   * are participants but nobody's correspondent.
   */
  from: string | null;
  /** Display names as the headers gave them, by address. Never looked up anywhere else. */
  names: Readonly<Record<string, string>>;
}

export interface Person {
  /** The lower-cased address, which is the identity: a name changes, an address does not (D53's rule). */
  email: string;
  /** The display name the headers used most often, or null when they only ever gave the address. */
  name: string | null;
  /** The domain, when it is not a public mailbox provider — an employer's name is not knowable from it. */
  organisation: string | null;
  firstSeen: Date;
  /**
   * The last contact that has **happened**. Never in the future: a meeting in the diary is not a sighting,
   * and `lastSeen` reading *next Tuesday* was the complaint that split this from `nextAt` (F304).
   */
  lastSeen: Date;
  /** The earliest thing arranged and not yet arrived — a meeting in the diary — or null if there is none. */
  nextAt: Date | null;
  /** Messages this person wrote. */
  mailsFrom: number;
  /** Messages this person received, from the account's owner or beside them. */
  mailsTo: number;
  meetings: number;
  /** Median days between contacts, once there are three; null before that, because two points are a line. */
  cadenceDays: number | null;
  /** Frequency, recency, reciprocity and meetings, folded into one number in [0, 1]. Inferred, not stated. */
  strength: number;
  /** Every pointer this person was derived from: the provenance, and what erasure follows (O38). */
  basis: string[];
}

/**
 * Domains that name a mailbox provider and not an organisation. `@gmail.com` says nothing about where
 * somebody works; `@fiktiv-leverandor.no` says quite a lot. Deliberately short: a domain missing from this
 * list makes somebody's *organisation* read as `gmail.com`, which is wrong in a way a person notices at
 * once, whereas a domain wrongly on it only withholds an inference.
 */
export const PUBLIC_MAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'hotmail.no',
  'live.com',
  'live.no',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'yahoo.com',
  'yahoo.no',
  'proton.me',
  'protonmail.com',
  'online.no',
  'broadpark.no',
  'getmail.no',
  'example.test',
]);

/** The organisation an address implies, or null: a public provider implies nothing. */
export function organisationOf(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  const domain = email.slice(at + 1).toLowerCase();
  return domain.length === 0 || PUBLIC_MAIL_DOMAINS.has(domain) ? null : domain;
}

const DAY = 86_400_000;

/** The middle value: robust to one long gap in a run of short ones, which is what a holiday looks like. */
export function median(values: readonly number[]): number | null {
  const n = values.length;
  if (n === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  // The two middle positions, which coincide for an odd count. Summed in a pass rather than indexed, so
  // there is no "and if the index is somehow empty" branch that no input can ever reach.
  const lo = Math.floor((n - 1) / 2);
  const hi = Math.floor(n / 2);
  return sorted.reduce((sum, v, i) => sum + (i === lo ? v : 0) + (i === hi ? v : 0), 0) / 2;
}

/**
 * How strong a tie is, from what a header can count. Four signals, each in [0, 1], averaged with meetings
 * weighted double: meeting somebody is a stronger signal than writing to them (Calendar.md), and a person
 * who only ever receives mail from the owner and never answers is not a relationship, whatever the count.
 *
 *  - frequency: contacts against a saturating scale, so the tenth contact adds less than the second;
 *  - recency: 1 for today, halving every ninety days;
 *  - reciprocity: how balanced the traffic is, 0 for one-way and 1 for even;
 *  - meetings: the same saturating scale over meetings alone.
 */
export function strengthOf(
  input: { mailsFrom: number; mailsTo: number; meetings: number; lastSeen: Date },
  now: Date,
): number {
  const contacts = input.mailsFrom + input.mailsTo + input.meetings;
  const saturating = (n: number) => 1 - Math.exp(-n / 5);
  const frequency = saturating(contacts);
  const ageDays = Math.max(0, (now.getTime() - input.lastSeen.getTime()) / DAY);
  const recency = Math.pow(0.5, ageDays / 90);
  const traffic = input.mailsFrom + input.mailsTo;
  const reciprocity = traffic === 0 ? 0 : 1 - Math.abs(input.mailsFrom - input.mailsTo) / traffic;
  const meetings = saturating(input.meetings);
  // Rounded to three places: a strength of 0.4217382 is false precision about a count of five.
  return Math.round(((frequency + recency + reciprocity + 2 * meetings) / 5) * 1000) / 1000;
}

/**
 * **A calendar's own address, which is nobody** (Thomas, 2026-10-19: *Google group calendars appear as people*). Google
 * names a shared or team calendar `…@group.calendar.google.com`, a room or other bookable resource
 * `…@resource.calendar.google.com`, a subscribed public one (holidays) `…@group.v.calendar.google.com` and an imported
 * one `…@import.calendar.google.com`: each turns up on an event as its organiser or a guest, and none is a person the
 * owner deals with. Every domain under `calendar.google.com`, so one Google adds later is caught too.
 */
export function isCalendarAddress(address: string): boolean {
  const domain = address.toLowerCase().trim().split('@')[1] ?? '';
  return domain === 'calendar.google.com' || domain.endsWith('.calendar.google.com');
}

/**
 * **A system or notification sender, which is nobody** (Thomas, 2026-10-20, from the connector after deploy 15: *system
 * and notification senders appear as people*). A shared document's notice comes from one no-reply address with each
 * sharer's name in front of it (*Kari (via Google Dokumenter)*), so one such address is many senders and its card was
 * nobody's. Read off the address alone, in two parts, each a pattern written once here and read by both this function
 * and the database (`listPeople` matches them with `~`, so the screen's list and its cards cannot disagree):
 *
 *  - **the part before the `@`**: a no-reply or do-not-reply mailbox, as a whole word or at the end of one
 *    (`drive-shares-dm-noreply`, `noreply`, `no.reply`), or one whose whole word is `notification(s)`, `notify`,
 *    `mailer-daemon`, `postmaster` or `bounce(s)`;
 *  - **the domain**: a label that is `bounce(s)`, `noreply`, `no-reply` or `notification(s)` (a provider's bounce or
 *    no-reply subdomain), or `docs.google.com`, where every address is a document's comment notice.
 *
 * A word inside another word is not one: *nora.replysen*, *bouncer* and *postmann* are people.
 */
export const SYSTEM_SENDER_LOCAL = String.raw`(^|[._+-])(no[-_.]?reply|do[-_.]?not[-_.]?reply)|(^|[._+-])(notifications?|notify|mailer-daemon|postmaster|bounces?)([._+-]|$)`;
export const SYSTEM_SENDER_DOMAIN = String.raw`(^|\.)(bounces?|no-?reply|notifications?)\.|(^|\.)docs\.google\.com$`;
const systemLocal = new RegExp(SYSTEM_SENDER_LOCAL);
const systemDomain = new RegExp(SYSTEM_SENDER_DOMAIN);

/** Whether an address is a system or notification sender (`SYSTEM_SENDER_LOCAL`, `SYSTEM_SENDER_DOMAIN`). */
export function isSystemSender(address: string): boolean {
  // Split as the database's `split_part(email, '@', 1|2)` splits, so the two readings agree on every address.
  const [local = '', domain = ''] = address.toLowerCase().trim().split('@');
  return systemLocal.test(local) || systemDomain.test(domain);
}

/**
 * The people a set of pointers yields. `self` is the account's own address(es): the owner is in every
 * header and is nobody's correspondent, and a derivation that forgot to exclude them would make the owner
 * their own strongest relationship.
 */
export function derivePeople(
  pointers: readonly Pointer[],
  self: readonly string[],
  now: Date,
): Person[] {
  const own = new Set(self.map((s) => s.toLowerCase()));
  const seen = new Map<
    string,
    {
      names: Map<string, number>;
      dates: Date[];
      mailsFrom: number;
      mailsTo: number;
      meetings: number;
      basis: Set<string>;
    }
  >();

  for (const pointer of pointers) {
    // A file's owners are participants in the table and nobody's correspondent here: sharing a document
    // is not a conversation, and counting it as one would make a shared drive look like a friendship.
    if (pointer.surface === 'drive') continue;
    /**
     * **An event this person is not on is somebody else's meeting** (F303).
     *
     * An account lists every calendar anybody has ever shared with it: a colleague's diary, a company's
     * team calendar, a fixture list. Counting their events meant `me.people` reported *meetings* with
     * people who have never been in a room with the owner — and `strength` is weighted by meetings, so
     * strangers outranked correspondents.
     *
     * Applied here as well as in the reader, and deliberately so: the reader stops such events arriving
     * from now on, and this is what makes the answer right for the ones already stored — and what holds
     * when a person sets a calendar to `read` on purpose, where *read it* is still not *count me as having
     * been at everything in it*.
     *
     * Skipped when nothing is known about the owner: with no address, *are they on it* has no answer, and
     * reading every event as before is the honest behaviour rather than silently dropping all of them.
     */
    if (
      pointer.surface === 'calendar' &&
      own.size > 0 &&
      !pointer.participants.some((p) => own.has(p.toLowerCase()))
    )
      continue;
    for (const raw of pointer.participants) {
      const email = raw.toLowerCase();
      if (
        own.has(email) ||
        !email.includes('@') ||
        isCalendarAddress(email) ||
        isSystemSender(email)
      )
        continue;
      let entry = seen.get(email);
      if (!entry) {
        entry = {
          names: new Map(),
          dates: [],
          mailsFrom: 0,
          mailsTo: 0,
          meetings: 0,
          basis: new Set(),
        };
        seen.set(email, entry);
      }
      entry.dates.push(pointer.at);
      entry.basis.add(pointer.id);
      const name = pointer.names[email]?.trim();
      if (name) entry.names.set(name, (entry.names.get(name) ?? 0) + 1);
      if (pointer.surface === 'calendar') entry.meetings += 1;
      // Lower-cased on both sides: a `From:` header written with capitals is the same person, and a
      // comparison that missed that counted their own mail as mail sent to them.
      else if (pointer.from?.toLowerCase() === email) entry.mailsFrom += 1;
      else entry.mailsTo += 1;
    }
  }

  const people: Person[] = [];
  const nowAt = now.getTime();
  for (const [email, entry] of seen) {
    const dates = entry.dates.map((d) => d.getTime()).sort((a, b) => a - b);
    /**
     * **Contact that has happened, and contact that is only arranged** (Thomas, 2026-10-02: *lastSeen is
     * past only*).
     *
     * A calendar pointer is ordered by when the event starts, so a meeting next Tuesday is an instant in
     * the future — and every date below was computed over all of them. `lastSeen` therefore read *next
     * Tuesday* for anybody with something in the diary, `cadenceDays` counted the gap to an appointment
     * nobody has been to yet, and `strengthOf` scored a stranger with one booking as more recent than
     * somebody written to this morning.
     *
     * So the two are separated: everything that describes contact is computed over the past, and what is
     * arranged is `nextAt`, which says so in its name.
     */
    const past = dates.filter((d) => d <= nowAt);
    const ahead = dates.filter((d) => d > nowAt);
    const gaps: number[] = [];
    let previous: number | null = null;
    let first = Number.POSITIVE_INFINITY;
    let last = Number.NEGATIVE_INFINITY;
    for (const current of past) {
      if (previous !== null) gaps.push((current - previous) / DAY);
      previous = current;
      first = Math.min(first, current);
      last = Math.max(last, current);
    }
    const cadence = median(gaps);
    // The name used most often. Ties go to the longer one, which is more likely the full name than the
    // short form — and a name is never invented: no header, no name.
    const name =
      [...entry.names.entries()].sort((a, b) => b[1] - a[1] || b[0].length - a[0].length)[0]?.[0] ??
      null;
    /**
     * An entry exists only because at least one pointer named this address — but every one of them may be
     * in the future, which is somebody Autho knows of only because there is a meeting arranged with them.
     * There has then been no contact, and the instant at which Autho holds something about them is now: the
     * invitation is in the data today, whatever date it is for. `nextAt` is what says when.
     */
    const firstSeen = past.length > 0 ? new Date(first) : now;
    const lastSeen = past.length > 0 ? new Date(last) : now;
    const soonest = ahead[0];
    people.push({
      email,
      name,
      organisation: organisationOf(email),
      firstSeen,
      lastSeen,
      nextAt: soonest === undefined ? null : new Date(soonest),
      mailsFrom: entry.mailsFrom,
      mailsTo: entry.mailsTo,
      meetings: entry.meetings,
      // Two contacts are a line, not a cadence: nothing is claimed until there are three.
      cadenceDays: cadence === null || past.length < 3 ? null : Math.round(cadence * 10) / 10,
      strength: strengthOf(
        { mailsFrom: entry.mailsFrom, mailsTo: entry.mailsTo, meetings: entry.meetings, lastSeen },
        now,
      ),
      basis: [...entry.basis].sort(),
    });
  }
  // Strongest first, then by address, so the order is the same for the same input.
  return people.sort((a, b) => b.strength - a.strength || (a.email < b.email ? -1 : 1));
}
